import React, { useState, useMemo, useRef, useEffect } from "react";
import { useStripeAppearance } from "@/lib/stripeAppearance";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Lock, Heart, X } from "lucide-react";
import { getStripe } from "@/lib/stripe";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { useJoinPrice } from "@/hooks/useJoinPrice";
import { CoinPrice, CopyWithCoinPrice, COIN_PRICE_SLOT } from "@/components/common/CoinPrice";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { trackClick, gaEcommerce, gaItem, trackPurchase } from "@/lib/track";
import { platformClient } from "@/lib/musicApi";
import { pageGet } from "@/lib/pageGet";
import { BuyCoinsPanel } from "@/components/wallet/BuyCoinsPanel";
import { coinsExact } from "@/lib/coins";
import { coinVars, balanceVars } from "@/lib/coins";
import { isRateLimited, RATE_LIMITED_KEY } from "@/lib/apiErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "var(--accent-primary)";
const MAX_CENTS = 50000; // $500 per-transaction cap (mirrors server TIP_TXN_CAP)
const TOKEN_KEY = "samtwo_platform_token";

// Playful rotating copy. Never the same line twice in a row (module-level memory).
// Defaults act as fallback until the API copy_lines load (fetched only where the
// copy line is actually shown - the non-compact path).
const COPY = [
  "Buy SAMTWO a coffee",
  "Slide into SAMTWO's bank account",
  "Fund the next banger",
  "Worth more than a Snickers? Send a tip.",
  "The music is free. The vibes are priceless. The tip is optional.",
  "Tip this poor guy",
];
const DEFAULT_POST_TIP = [
  "Certified boss move. SAMTWO owes you a coffee now.",
  "That just bought some studio time. Or snacks. Mostly snacks.",
  "You're officially his favorite. Don't tell the others.",
];
let lastCopyIdx = -1;
const pickCopy = (arr) => {
  const list = arr && arr.length ? arr : COPY;
  let i = Math.floor(Math.random() * list.length);
  if (i === lastCopyIdx) i = (i + 1) % list.length;
  lastCopyIdx = i;
  return list[i];
};

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

// Stripe error/decline codes -> our translated keys. We never render Stripe's raw
// err.message. Anything unmapped falls through to tips.payError.
const STRIPE_ERR = {
  card_declined: "tips.errCardDeclined",
  insufficient_funds: "tips.errInsufficientFunds",
  expired_card: "tips.errExpiredCard",
  incorrect_cvc: "tips.errIncorrectCvc",
  invalid_cvc: "tips.errIncorrectCvc",
  incorrect_number: "tips.errIncorrectNumber",
  invalid_number: "tips.errIncorrectNumber",
  processing_error: "tips.errProcessingError",
};

// Backend tip reason codes -> our translated keys. The response `message` is kept
// server-side for logging only; it is never rendered.
const TIP_BLOCKED_REASON = {
  per_transaction_limit: "tips.capWarning",
  payment_not_completed: "tips.payIncomplete",
};

// Reason codes from the /tips/create 400 (cap enforced before any PaymentIntent).
const TIP_START_REASON = {
  per_transaction_limit: "tips.capWarning",
};

// Stripe Elements supports these locales; hi/bn/ur are NOT supported, so we omit
// the option for them and let Stripe use its default rather than a rejected code.
const STRIPE_LOCALES = { fr: "fr", es: "es", pt: "pt", ru: "ru", ar: "ar", zh: "zh" };

// Phase 2: card entry, mounted once we have a client secret.
const CardStep = ({ amountCents, savable, paymentIntentId, onDone }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [save, setSave] = useState(true);

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError("");
    // Our own pre-checked save box: if the buyer unticked it, clear the save
    // intent BEFORE confirming. The charge itself stays browser-confirmed.
    if (savable && !save) {
      try { await axios.post(`${API}/payments/save-preference`, { payment_intent_id: paymentIntentId, save: false }, { headers: authHeaders() }); } catch { /* non-blocking */ }
    }
    const { error: err, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (err) {
      setError(t(STRIPE_ERR[err.decline_code] || STRIPE_ERR[err.code] || "tips.payError"));
      setBusy(false);
      return;
    }
    // Automatic capture: a successful confirmPayment settles the charge and the
    // PaymentIntent status becomes "succeeded" (no separate capture step).
    if (paymentIntent && (paymentIntent.status === "requires_capture" || paymentIntent.status === "succeeded")) {
      trackPurchase({ eventId: paymentIntent.id, category: "tip", purchaseType: "tip",
        items: [gaItem({ id: "tip", name: "tip", category: "tip", priceCents: paymentIntent.amount })],
        valueCents: paymentIntent.amount, currency: (paymentIntent.currency || "usd").toUpperCase() });
      try {
        const { data } = await axios.post(`${API}/tips/confirm`, { payment_intent_id: paymentIntent.id }, { headers: authHeaders() });
        onDone(data);
      } catch {
        setError(t("tips.confirmError"));
        setBusy(false);
      }
      return;
    }
    setError(t("tips.payIncomplete"));
    setBusy(false);
  };

  return (
    <form onSubmit={pay} data-testid="tip-card-form" className="mt-3">
      <PaymentElement options={{ layout: "tabs" }} />
      {savable && (
        <label className="flex items-center gap-2 mt-3 cursor-pointer select-none" data-testid="tip-save-card-label">
          <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} data-testid="tip-save-card" />
          <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("billing.saveCardForReuse")}</span>
        </label>
      )}
      {error && <p className="text-sm mt-3" style={{ color: "#ef4444" }} data-testid="tip-error">{error}</p>}
      <button type="submit" disabled={!stripe || busy} data-testid="tip-pay-btn"
        className="w-full mt-4 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 metallic-sheen"
        style={{ color: "#fff", opacity: busy ? 0.7 : 1 }}>
        {busy ? <><Loader2 size={16} className="animate-spin" /> {t("tips.processing")}</> : <><Lock size={14} /> <CopyWithCoinPrice template={t("tips.sendAmount", { amount: COIN_PRICE_SLOT })} cents={amountCents} size="sm" /></>}
      </button>
    </form>
  );
};

export const TipWidget = ({ sourceType = "footer", sourceId = null, compact = false, trigger = null }) => {
  const stripeAppearance = useStripeAppearance();
  const { t, i18n } = useTranslation();
  const { platformUser } = usePlatformAuth();
  const { resolveCopy } = useJoinPrice();
  const loggedIn = !!platformUser;
  const [copyLines, setCopyLines] = useState(null);
  const [postTipTexts, setPostTipTexts] = useState(null);
  const fetchedRef = useRef(false);
  // Fetch tip-widget content lazily. copy_lines are only shown in the non-compact
  // path, so fetch on mount there; compact pills fetch on open (so post_tip_texts
  // are ready before a tip can complete) and never fire a GET if left untouched.
  const fetchContent = () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    axios.get(`${API}/home/tip-widget`).then(({ data }) => {
      if (Array.isArray(data.copy_lines) && data.copy_lines.length) setCopyLines(data.copy_lines);
      if (Array.isArray(data.post_tip_texts) && data.post_tip_texts.length) setPostTipTexts(data.post_tip_texts);
    }).catch(() => {});
  };
  useEffect(() => { if (!compact) fetchContent(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [compact]);
  const copy = useMemo(() => pickCopy(copyLines || COPY), [copyLines]);
  const postTipMsg = useMemo(() => {
    const arr = postTipTexts && postTipTexts.length ? postTipTexts : DEFAULT_POST_TIP;
    return arr[Math.floor(Math.random() * arr.length)];
  }, [postTipTexts]);
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([300, 400, 500]);
  const [amount, setAmount] = useState(300);
  useEffect(() => {
    pageGet(`${API}/settings/pricing`).then((data) => {
      if (Array.isArray(data?.tip_amounts) && data.tip_amounts.length === 3) {
        const cents = data.tip_amounts.map((d) => Math.round(parseFloat(d) * 100));
        setPresets(cents); setAmount((a) => (a === 300 ? cents[0] : a));
      }
    });
  }, []);
  const [custom, setCustom] = useState("");
  const [anon, setAnon] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [starting, setStarting] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [customerSessionSecret, setCustomerSessionSecret] = useState("");
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [piId, setPiId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [wallet, setWallet] = useState(null);
  const [useCoins, setUseCoins] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [coinDecline, setCoinDecline] = useState(null);
  const started = useRef(false);

  const amountCents = custom ? Math.round(parseFloat(custom) * 100) : amount;
  const overCap = Number.isFinite(amountCents) && amountCents > MAX_CENTS;

  const loadWallet = React.useCallback(() => {
    if (!loggedIn) { setWallet(null); return; }
    platformClient.get("/coins/wallet").then(({ data }) => setWallet(data)).catch(() => setWallet(null));
  }, [loggedIn]);
  useEffect(() => { if (open) loadWallet(); }, [open, loadWallet]);
  const coinsEligible = loggedIn && !!wallet;
  const spendableCents = wallet ? (wallet.spendable_cents ?? wallet.balance_cents) : 0;
  const coinsCover = coinsEligible && Number.isFinite(amountCents) && amountCents >= 100 && spendableCents >= amountCents;
  useEffect(() => { setUseCoins(coinsCover); }, [coinsCover]);
  // A changed amount invalidates any prior server decline.
  useEffect(() => { setCoinDecline(null); }, [amountCents]);

  const reset = () => {
    setOpen(false); setClientSecret(""); setCustomerSessionSecret(""); setHasSavedCard(false); setPiId(""); setResult(null); setError("");
    setCustom(""); setAnon(false); setFirst(""); setLast(""); setAmount(300); setCoinDecline(null);
    started.current = false;
  };

  const start = async () => {
    setError(""); setCoinDecline(null);
    if (!amountCents || amountCents < 100) { setError(t("tips.minError")); return; }
    if (amountCents > MAX_CENTS) { setError(t("tips.capWarning")); return; }
    if (!loggedIn && (!first.trim() || !last.trim())) { setError(t("tips.nameError")); return; }
    if (started.current) return;
    trackClick("tip_button", { source: sourceType, amount_cents: amountCents });
    started.current = true;
    setStarting(true);
    try {
      // Coins: no Stripe round-trip. The server spends the wallet, records the tip and
      // fires the artist notification in one call, then returns paid_with:"coins".
      if (useCoins && coinsEligible) {
        if (!coinsCover) { started.current = false; setStarting(false); return; }
        const { data } = await axios.post(`${API}/tips/create`, {
          amount_cents: amountCents, source_type: sourceType, source_id: sourceId,
          is_anonymous: anon, use_coins: true,
        }, { headers: authHeaders() });
        if (data.coin_error) {
          // Hold the decline as data, not a pre-formatted string: tips.coins.needMore
          // carries an <a> tag and must render through <Trans>, never as plain text.
          setUseCoins(false);
          setCoinDecline({ balance: coinsExact(data.spendable_cents || 0), needed: t("coins.perPrice", coinVars(Math.ceil((data.amount_cents || amountCents) / 20))) });
          started.current = false; setStarting(false); return;
        }
        if (data.paid_with === "coins") { setResult({ status: "succeeded" }); setStarting(false); return; }
      }
      const { data } = await axios.post(`${API}/tips/create`, {
        amount_cents: amountCents, source_type: sourceType, source_id: sourceId,
        is_anonymous: anon,
        guest_first_name: loggedIn ? null : first.trim(),
        guest_last_name: loggedIn ? null : last.trim(),
      }, { headers: authHeaders() });
      setClientSecret(data.client_secret);
      setCustomerSessionSecret(data.customer_session_client_secret || "");
      setHasSavedCard(!!data.has_saved_card);
      setPiId(data.payment_intent_id || "");
      gaEcommerce("begin_checkout", { items: [gaItem({ id: "tip", name: "tip", category: "tip", priceCents: amountCents })], valueCents: amountCents, checkout_type: "tip" });
    } catch (e) {
      const reason = e?.response?.data?.detail?.reason;
      setError(t(isRateLimited(e) ? RATE_LIMITED_KEY : (TIP_START_REASON[reason] || "tips.startError")));
      started.current = false;
    } finally { setStarting(false); }
  };

  // Shared form/result body (identical in inline and modal modes).
  const formBody = result ? (
    <div data-testid="tip-result" className="text-center py-4">
      {result.status === "succeeded" ? (
        <>
          <Heart size={28} className="mx-auto mb-3" style={{ color: ACCENT }} />
          <p className="text-base font-bold mb-1" style={{ color: "var(--text-primary)" }} data-testid="tip-result-success">{t("tips.thankYou")}</p>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="tip-result-message">{resolveCopy(postTipMsg)}</p>
        </>
      ) : (
        <>
          <p className="text-base font-bold mb-1" style={{ color: "var(--text-primary)" }} data-testid="tip-result-blocked">{t("tips.blockedTitle")}</p>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{t(TIP_BLOCKED_REASON[result.reason] || "tips.blockedFallback")}</p>
        </>
      )}
      <button onClick={reset} className="mt-4 text-sm font-semibold" style={{ color: ACCENT }} data-testid="tip-done-btn">{t("music.close")}</button>
    </div>
  ) : clientSecret ? (
    <>
      <p className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}><CopyWithCoinPrice template={t("tips.tipping", { amount: COIN_PRICE_SLOT })} cents={amountCents} size="sm" /></p>
      <Elements stripe={getStripe()} options={{ clientSecret, customerSessionClientSecret: customerSessionSecret || undefined, locale: STRIPE_LOCALES[i18n.language] || undefined, appearance: stripeAppearance }}>
        <CardStep amountCents={amountCents} savable={!!customerSessionSecret && !hasSavedCard} paymentIntentId={piId} onDone={setResult} />
      </Elements>
    </>
  ) : (
    <>
      <p className="text-base font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("tips.sendHeading", { name: "SAMTWO" })}</p>
      <div className="flex gap-2 mb-3">
        {presets.map((p) => {
          const active = !custom && amount === p;
          return (
            <button key={p} onClick={() => { setAmount(p); setCustom(""); }} data-testid={`tip-preset-${p}`}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${active ? "metallic-sheen" : ""}`}
              style={active
                ? { color: "#fff" }
                : { background: "var(--bg-subtle)", color: "var(--text-primary)", border: "1px solid var(--border-color)" }}>
              {<CoinPrice cents={p} size="xs" tone="inherit" />}
            </button>
          );
        })}
      </div>
      <div className="relative mb-3">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--text-secondary)" }}>$</span>
        <input type="number" min="1" step="0.01" value={custom} onChange={(e) => setCustom(e.target.value)}
          placeholder={t("tips.customAmount")} data-testid="tip-custom-input"
          className="w-full pl-7 pr-3 py-2.5 rounded-xl text-sm outline-none"
          style={{ background: "var(--bg-subtle)", border: `1px solid ${overCap ? "#ef4444" : (custom ? ACCENT : "var(--border-color)")}`, color: "var(--text-primary)" }} />
        {overCap ? (
          <p className="text-xs mt-2" style={{ color: "#ef4444" }} data-testid="tip-cap-warning">
            {t("tips.capWarning")}
          </p>
        ) : (custom && Number.isFinite(amountCents) && amountCents >= 100 ? (
          <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }} data-testid="tip-custom-coins">
            <CoinPrice cents={amountCents} size="sm" tone="inherit" />
          </p>
        ) : null)}
      </div>

      {!loggedIn && (
        <div className="flex gap-2 mb-3">
          <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder={t("tips.firstName")} data-testid="tip-guest-first"
            className="flex-1 px-3 py-2.5 rounded-xl text-sm outline-none" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
          <input value={last} onChange={(e) => setLast(e.target.value)} placeholder={t("tips.lastName")} data-testid="tip-guest-last"
            className="flex-1 px-3 py-2.5 rounded-xl text-sm outline-none" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
        </div>
      )}

      <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
        <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="tip-anon-checkbox" />
        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("tips.anon")}</span>
      </label>

      {coinsEligible && (
        <div className="mb-4" data-testid="tip-coins">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={useCoins} onChange={(e) => setUseCoins(e.target.checked)} data-testid="tip-use-coins" />
            <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{t("tips.coins.toggleLabel")}</span>
          </label>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }} data-testid="tip-coins-balance">{t("tips.coins.balanceLine", balanceVars(wallet.spendable_coins ?? wallet.balance_coins))}</p>
          {useCoins && coinsCover && (
            <p className="text-sm mt-1" style={{ color: "var(--accent-primary)" }} data-testid="tip-coins-covers">{t("tips.coins.covers")}</p>
          )}
          {useCoins && !coinsCover && amountCents >= 100 && (
            <p className="text-sm mt-1" style={{ color: "var(--text-primary)" }} data-testid="tip-coins-need-more">
              {wallet.balance_cents < 0 ? (
              <Trans i18nKey="coins.negativeOwing" components={{ a: <button type="button" onClick={() => setBuyOpen(true)} data-testid="tip-coin-buy" className="underline font-semibold" style={{ color: "var(--accent-primary)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }} /> }} />
              ) : (
              <Trans i18nKey="tips.coins.needMore" values={{ ...balanceVars(wallet.spendable_coins ?? wallet.balance_coins), needed: t("coins.perPrice", coinVars(Math.ceil(amountCents / 20))) }} components={{ a: <button type="button" onClick={() => setBuyOpen(true)} data-testid="tip-coin-buy" className="underline font-semibold" style={{ color: "var(--accent-primary)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }} /> }} />
              )}
            </p>
          )}
          {useCoins && (
            <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }} data-testid="tip-coins-final">{t("tips.coins.final")}</p>
          )}
        </div>
      )}

      {coinDecline && (
        <p className="text-sm mb-3" style={{ color: "var(--text-primary)" }} data-testid="tip-coin-decline">
          <Trans i18nKey="tips.coins.needMore" values={{ ...balanceVars(coinDecline.balance), needed: coinDecline.needed }} components={{ a: <button type="button" onClick={() => setBuyOpen(true)} data-testid="tip-coin-buy-decline" className="underline font-semibold" style={{ color: "var(--accent-primary)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }} /> }} />
        </p>
      )}

      {error && <p className="text-sm mb-3" style={{ color: "#ef4444" }} data-testid="tip-error">{error}</p>}

      <button onClick={start} disabled={starting || overCap || (useCoins && coinsEligible && !coinsCover)} data-testid="tip-send-btn"
        className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 metallic-sheen"
        style={{ color: "#fff", opacity: (starting || overCap || (useCoins && coinsEligible && !coinsCover)) ? 0.5 : 1, cursor: overCap ? "not-allowed" : "pointer" }}>
        {starting ? <><Loader2 size={16} className="animate-spin" /> {t("tips.starting")}</> : <CopyWithCoinPrice template={t("tips.sendBtnAmount", { amount: COIN_PRICE_SLOT })} cents={amountCents} size="sm" />}
      </button>
      <BuyCoinsPanel open={buyOpen} onOpenChange={setBuyOpen} onCredited={() => loadWallet()} />
    </>
  );

  // Compact: icon-only trigger that opens the form in a modal overlay (tight spaces).
  // An optional `trigger` render-prop lets callers supply their own styled button
  // while reusing this same Dialog + tip form (e.g. the homepage hero CTA).
  if (compact) {
    return (
      <>
        {trigger ? (
          trigger({ onClick: () => { fetchContent(); setOpen(true); } })
        ) : (
          <button onClick={() => { fetchContent(); setOpen(true); }} data-testid="tip-open-btn" title={t("tips.sendHeading", { name: "SAMTWO" })} aria-label={t("tips.sendHeading", { name: "SAMTWO" })}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all hover:-translate-y-0.5 metallic-sweep-overlay"
            style={{ backgroundColor: "var(--bg-surface)", border: "1px solid var(--border-color)", color: ACCENT }}>
            <Heart size={13} /> {t("tips.tipShort")}
          </button>
        )}
        <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); }}>
          <DialogContent data-testid="tip-widget-expanded" className="max-w-lg"
            style={{ maxHeight: "92vh", display: "flex", flexDirection: "column", padding: 0, gap: 0, overflow: "hidden" }}>
            <DialogTitle className="sr-only">{t("tips.sendHeading", { name: "SAMTWO" })}</DialogTitle>
            <DialogDescription className="sr-only">{t("tips.sendDesc", { name: "SAMTWO" })}</DialogDescription>
            <div className="overflow-y-auto p-6" style={{ flex: 1 }} data-testid="tip-scroll">
              {formBody}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Full (inline): collapsed pill row, expands into an inline card.
  if (!open) {
    return (
      <div data-testid="tip-widget" className="flex flex-col sm:flex-row items-center gap-3">
        <span className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="tip-copy">{resolveCopy(copy)}</span>
        <button onClick={() => setOpen(true)} data-testid="tip-open-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5 metallic-sheen"
          style={{ color: "#fff" }}>
          <Heart size={15} /> {t("tips.sendBtn")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="tip-widget-expanded" className="w-full max-w-lg rounded-2xl p-6 relative"
      style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
      <button onClick={reset} data-testid="tip-close" className="absolute top-3 right-3 p-1" style={{ color: "var(--text-secondary)" }}><X size={18} /></button>
      {formBody}
    </div>
  );
};

export default TipWidget;
